/**
 * Convert Markdown to RTF (Rich Text Format)
 * Supports basic formatting: bold, italic, headers, lists, code blocks
 */

export function markdownToRtf(markdown: string): string {
  // RTF header with font table and color table
  const rtfHeader = `{\\rtf1\\ansi\\deff0
{\\fonttbl{\\f0\\fnil\\fcharset0 Calibri;}{\\f1\\fmodern\\fcharset0 Courier New;}}
{\\colortbl;\\red0\\green0\\blue0;\\red51\\green51\\blue51;\\red0\\green0\\blue255;}
\\viewkind4\\uc1\\pard\\sa200\\sl276\\slmult1\\f0\\fs22 `;

  const rtfFooter = `}`;

  let rtfContent = markdown;

  // Convert headers (# Header -> \b\fs32 Header\b0\fs22\par)
  rtfContent = rtfContent.replace(/^### (.+)$/gm, '\\b\\fs26 $1\\b0\\fs22\\par\\par');
  rtfContent = rtfContent.replace(/^## (.+)$/gm, '\\b\\fs28 $1\\b0\\fs22\\par\\par');
  rtfContent = rtfContent.replace(/^# (.+)$/gm, '\\b\\fs32 $1\\b0\\fs22\\par\\par');

  // Convert bold (**text** or __text__ -> \b text\b0)
  rtfContent = rtfContent.replace(/\*\*(.+?)\*\*/g, '\\b $1\\b0');
  rtfContent = rtfContent.replace(/__(.+?)__/g, '\\b $1\\b0');

  // Convert italic (*text* or _text_ -> \i text\i0)
  rtfContent = rtfContent.replace(/\*(.+?)\*/g, '\\i $1\\i0');
  rtfContent = rtfContent.replace(/_(.+?)_/g, '\\i $1\\i0');

  // Convert code blocks (```code``` -> monospace font)
  rtfContent = rtfContent.replace(/```[\s\S]*?```/g, (match) => {
    const code = match.replace(/```/g, '').trim();
    return `\\f1\\fs20 ${code.replace(/\n/g, '\\par\n')}\\f0\\fs22\\par`;
  });

  // Convert inline code (`code` -> monospace)
  rtfContent = rtfContent.replace(/`(.+?)`/g, '\\f1 $1\\f0');

  // Convert unordered lists (- item or * item -> bullet points)
  rtfContent = rtfContent.replace(/^[\-\*] (.+)$/gm, '\\bullet  $1\\par');

  // Convert ordered lists (1. item -> numbered)
  let listNumber = 1;
  rtfContent = rtfContent.replace(/^\d+\. (.+)$/gm, () => {
    const result = `${listNumber}.  $1\\par`;
    listNumber++;
    return result;
  });

  // Convert line breaks
  rtfContent = rtfContent.replace(/\n\n/g, '\\par\\par\n');
  rtfContent = rtfContent.replace(/\n/g, '\\par\n');

  // Escape special RTF characters
  rtfContent = rtfContent.replace(/\\/g, '\\\\');
  rtfContent = rtfContent.replace(/{/g, '\\{');
  rtfContent = rtfContent.replace(/}/g, '\\}');

  return rtfHeader + rtfContent + rtfFooter;
}

/**
 * Download content as a file
 */
export function downloadFile(content: string, filename: string, mimeType: string) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Replace the extension on a filename with one derived from the blob's
 * content-type. Returns the original filename when we can't determine a
 * better extension (unknown mime, no dot in filename, etc.).
 */
function reconcileFilenameWithBlob(filename: string, blob: Blob): string {
  const blobMime = blob.type?.toLowerCase();
  const blobExt = blobMime ? MIME_TO_EXT[blobMime] : undefined;
  if (!blobExt) return filename;

  const dot = filename.lastIndexOf('.');
  if (dot < 0) return `${filename}.${blobExt}`;

  const currentExt = filename.slice(dot + 1).toLowerCase();
  if (currentExt === blobExt) return filename;
  // jpg/jpeg are interchangeable — don't rewrite one to the other.
  if ((currentExt === 'jpg' && blobExt === 'jpeg') || (currentExt === 'jpeg' && blobExt === 'jpg')) {
    return filename;
  }
  return `${filename.slice(0, dot)}.${blobExt}`;
}

/**
 * Download image from data URL or blob URL
 */
export async function downloadImage(imageUrl: string, filename: string) {
  try {
    // If it's a data URL, we can download directly
    if (imageUrl.startsWith('data:')) {
      const link = document.createElement('a');
      link.href = imageUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      return;
    }

    // If it's a blob URL or regular URL, fetch and download
    const response = await fetch(imageUrl);
    const blob = await response.blob();
    const finalFilename = reconcileFilenameWithBlob(filename, blob);
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = finalFilename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Failed to download image:', error);
    throw new Error('Failed to download image');
  }
}

/**
 * Download video from URL
 */
export async function downloadVideo(videoUrl: string, filename: string) {
  try {
    // If it's a blob URL, download directly
    if (videoUrl.startsWith('blob:')) {
      const link = document.createElement('a');
      link.href = videoUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      return;
    }

    // Otherwise fetch and download
    const response = await fetch(videoUrl);
    const blob = await response.blob();
    const finalFilename = reconcileFilenameWithBlob(filename, blob);
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = finalFilename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Failed to download video:', error);
    throw new Error('Failed to download video');
  }
}

const VALID_EXTENSIONS = ['png','jpg','jpeg','webp','gif','mp4','webm','mov','mp3','wav','opus','ogg'];

const MIME_TO_EXT: Record<string, string> = {
  'image/jpeg': 'jpg',
  'image/jpg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
  'image/gif': 'gif',
  'video/mp4': 'mp4',
  'video/webm': 'webm',
  'video/quicktime': 'mov',
  // ElevenLabs output formats — see audioFormatMeta() in server/routes/elevenlabs.ts
  'audio/mpeg': 'mp3',
  'audio/wav': 'wav',
  'audio/ogg': 'opus',
  'audio/basic': 'wav',
};

/**
 * Extract a file extension from any URL shape we encounter:
 *  - direct: foo.jpg
 *  - query string: /api/media/proxy?key=users/.../foo.jpg
 *  - data URL: data:image/jpeg;base64,...
 */
function extractExtensionFromUrl(url: string): string | undefined {
  if (!url) return undefined;

  // Data URL — read the mime type
  if (url.startsWith('data:')) {
    const m = url.match(/^data:([^;]+)/);
    if (m) return MIME_TO_EXT[m[1].toLowerCase()];
    return undefined;
  }

  // Pull candidates from both the path and any query-string values (covers
  // /api/media/proxy?key=users/.../foo.jpg where the real extension lives in
  // the query, not the path).
  const candidates: string[] = [];
  try {
    const u = new URL(url, 'http://placeholder.local');
    candidates.push(u.pathname);
    u.searchParams.forEach((v) => candidates.push(decodeURIComponent(v)));
  } catch {
    candidates.push(url.split('?')[0]);
  }

  for (const s of candidates) {
    const ext = s.split('.').pop()?.toLowerCase();
    if (ext && VALID_EXTENSIONS.includes(ext)) return ext;
  }
  return undefined;
}

/**
 * Generate a human-readable filename for downloaded media.
 * Format: {slug}-{YYYY-MM-DD}-{n}.{ext}
 * Examples: knods-2026-02-24.png  |  portrait-2026-02-24-3.mp4
 */
export function generateMediaFilename(
  type: 'image' | 'video' | 'audio',
  options?: { nodeTitle?: string; pageIndex?: number; url?: string; mimeType?: string }
): string {
  const fallbackExt = type === 'video' ? 'mp4' : type === 'audio' ? 'mp3' : 'png';

  // Prefer explicit mime type if provided (most reliable — comes from the blob).
  // Otherwise try to extract from the URL/query string.
  const mimeExt = options?.mimeType ? MIME_TO_EXT[options.mimeType.toLowerCase()] : undefined;
  const resolvedExt = mimeExt || extractExtensionFromUrl(options?.url ?? '') || fallbackExt;

  // Build slug from nodeTitle or fallback to 'knods'
  const rawTitle = (options?.nodeTitle ?? '').trim();
  const slug = rawTitle
    ? rawTitle.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 32)
    : 'knods';

  // Date stamp YYYY-MM-DD
  const d = new Date();
  const date = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

  // Only add index suffix when there are multiple pages (pageIndex > 0 or explicitly provided and > 0)
  const idx = options?.pageIndex;
  const suffix = idx !== undefined && idx > 0 ? `-${idx + 1}` : '';

  return `${slug}-${date}${suffix}.${resolvedExt}`;
}
